// Comprehension API Service for DeepSeek Integration
import { formatComprehensionPrompt, parseComprehensionResponse } from '../data/comprehensionData';

class ComprehensionApiService {
  constructor() {
    this.baseUrl = 'https://api.deepseek.com';
    this.apiKey = 'sk-7549b29d010a424a836d081a478552f4';
    this.model = 'deepseek-chat';
  }

  async generateComprehensionQuestions(passage, quizConfig) {
    try {
      if (!this.apiKey) {
        throw new Error('DeepSeek API key not configured');
      }

      const prompt = formatComprehensionPrompt(passage, quizConfig);
      
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'You are an expert English language teacher specializing in reading comprehension. Generate high-quality, educational questions that test various aspects of reading comprehension including main ideas, details, vocabulary, inference, and critical thinking.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 4000,
          top_p: 0.9,
          frequency_penalty: 0.1,
          presence_penalty: 0.1
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response format');
      }

      const content = data.choices[0].message.content;
      console.log('DeepSeek API Response Content:', content);
      
      const parsedResponse = parseComprehensionResponse(content);
      
      // If parsing failed, try to generate mock questions as fallback
      if (!parsedResponse.success) {
        console.log('API parsing failed, using mock questions as fallback');
        return this.generateMockQuestions(passage, quizConfig);
      }
      
      // Add metadata
      parsedResponse.metadata = {
        ...parsedResponse.metadata,
        generationTime: `${data.usage?.total_tokens || 0} tokens`,
        model: this.model,
        timestamp: new Date().toISOString()
      };

      return parsedResponse;

    } catch (error) {
      console.error('Comprehension API Error:', error);
      return {
        success: false,
        error: error.message,
        questions: [],
        metadata: {
          generationTime: '0s',
          model: this.model,
          timestamp: new Date().toISOString()
        }
      };
    }
  }

  async validateQuestionFormat(question) {
    try {
      const validationPrompt = `Validate this reading comprehension question format and return JSON response:
      
Question: ${JSON.stringify(question)}

Requirements:
1. Check if all required fields are present
2. Validate question type is supported
3. Ensure correct answer format matches question type
4. Verify options array for multiple choice questions
5. Return validation result as JSON

Expected response format:
{
  "valid": true/false,
  "errors": ["error1", "error2"],
  "suggestions": ["suggestion1", "suggestion2"]
}`;

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'You are a validation expert for educational content. Analyze question formats and provide detailed feedback.'
            },
            {
              role: 'user',
              content: validationPrompt
            }
          ],
          temperature: 0.3,
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        throw new Error(`Validation API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        return {
          valid: false,
          errors: ['Failed to parse validation response'],
          suggestions: []
        };
      }

    } catch (error) {
      console.error('Question validation error:', error);
      return {
        valid: false,
        errors: [error.message],
        suggestions: []
      };
    }
  }

  async enhanceQuestion(question, passage) {
    try {
      const enhancementPrompt = `Enhance this reading comprehension question to make it more educational and challenging:

Original Question: ${JSON.stringify(question)}
Passage Context: ${passage.substring(0, 500)}...

Requirements:
1. Improve question clarity and educational value
2. Ensure it tests appropriate comprehension skills
3. Make distractors more plausible for multiple choice
4. Add or improve explanation
5. Return enhanced question in JSON format

Expected response format:
{
  "enhancedQuestion": {
    "questionNumber": 1,
    "paragraph": 1,
    "type": "MC",
    "question": "Enhanced question text",
    "options": ["A", "B", "C", "D"],
    "correctAnswer": 0,
    "explanation": "Enhanced explanation"
  },
  "improvements": ["improvement1", "improvement2"]
}`;

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: 'You are an expert in educational content development. Enhance questions to improve learning outcomes.'
            },
            {
              role: 'user',
              content: enhancementPrompt
            }
          ],
          temperature: 0.6,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`Enhancement API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        return {
          enhancedQuestion: question,
          improvements: ['Failed to parse enhancement response']
        };
      }

    } catch (error) {
      console.error('Question enhancement error:', error);
      return {
        enhancedQuestion: question,
        improvements: [error.message]
      };
    }
  }

  // Mock method for testing when API is not available
  async generateMockQuestions(passage, quizConfig) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          questions: [
            {
              id: 'mock-1',
              questionNumber: 1,
              paragraph: 1,
              type: 'MC',
              question: 'What is the main purpose of this text?',
              options: [
                'To inform',
                'To persuade',
                'To entertain',
                'To describe'
              ],
              correctAnswer: 0,
              explanation: 'This text primarily aims to inform the reader about the topic.'
            },
            {
              id: 'mock-2',
              questionNumber: 2,
              paragraph: 2,
              type: 'FB',
              question: 'Complete the sentence: The author states that _____ is important.',
              correctAnswer: 'comprehension',
              explanation: 'The text emphasizes the importance of comprehension skills.'
            }
          ],
          metadata: {
            totalQuestions: 2,
            generationTime: '1.2s',
            difficulty: 'intermediate',
            model: 'mock-model',
            timestamp: new Date().toISOString()
          }
        });
      }, 2000);
    });
  }
}

// Create singleton instance
const comprehensionApiService = new ComprehensionApiService();

export default comprehensionApiService; 